import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import ability from '@/libs/acl/ability'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  const refListTable = ref(null)
  const toast = useToast()

  const tableColumns = [
    { label: 'pacom.masterAccount', key: 'master_account.username', sortable: true },
    { label: 'pacom.site', key: 'site.name'},
    { label: 'pacom.schema_type', key: 'schema_type.display_name'},
    { label: 'pacom.gateway_id', key: 'gateway_id', sortable: true },
    { label: 'pacom.name', key: 'name', sortable: true },
    { label: 'pacom.status', key: 'status' },
  ]

  const masterAccounts = ref([]);
  const sites = ref([]);

  const masterAccountFilter = ref(null)
  const siteFilter = ref(null)

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const filters = ref({});

  const filtersCount = useFilterCounter(filters);

  const buildFilters = () => {
    filters.value = {
      masterAccount: masterAccountFilter.value,
      site: siteFilter.value
    }

    refetchData();
  }

  const clearSearchFilters = () => {
    masterAccountFilter.value = null;
    siteFilter.value = null;

    buildFilters();
  }

  const fetchList = (ctx, callback) => {
    store.dispatch('pacom_devices/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      masterAccount: masterAccountFilter.value,
      site: siteFilter.value,
    })
      .then(response => {
        callback(response.data)
        totalList.value = response.meta.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const fetchMasterAccounts = async () => {
    await store.dispatch('pacom_master_accounts/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      isList: true
    })
      .then(response => {
        masterAccounts.value = response.data;
      })
      .catch(() => {
        masterAccounts.value = []

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const fetchSites = async () => {
    await store.dispatch('pacom_sites/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      isList: true
    })
      .then(response => {
        sites.value = response.data;
      })
      .catch(() => {
        sites.value = []

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    fetchMasterAccounts,
    fetchSites,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    masterAccountFilter,
    siteFilter,
    refetchData,
    masterAccounts,
    sites,
    filtersCount,
    clearSearchFilters,
    buildFilters
  }
}
